import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Sparkles, Activity, Target, ArrowRight, ShieldCheck } from "lucide-react";
import PageShell from "../components/layout/PageShell";
import { getProjectIntelligence } from "../services/projectIntelligenceService";
import toast from "react-hot-toast";
import {
    isSuperAdminUser,
    canUseProjectDiagnostics
} from "../services/authService";
import { fixWebsite, fixWebsiteSection } from "../services/crmService";

import "./project.css";

export default function ProjectIntelligencePage() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [runningAction, setRunningAction] = useState("");

    const isSuperAdmin = isSuperAdminUser();
    const canRunProjectActions = canUseProjectDiagnostics();

    const load = useCallback(async () => {
        if (!projectId) return;

        try {
            setLoading(true);
            const res = await getProjectIntelligence(projectId);
            setData(res);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load project intelligence.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const runAction = async (actionKey) => {
        if (actionKey === "open-crm") {
            navigate(`/crm/projects/${projectId}`);
            return;
        }

        if (!canRunProjectActions) {
            toast.error("You do not have permission to run project improvement actions.");
            return;
        }

        try {
            setRunningAction(actionKey);

            if (actionKey === "improve-site") {
                await fixWebsite(projectId);
            } else if (actionKey === "improve-cta") {
                await fixWebsiteSection(projectId, {
                    sectionKey: "hero",
                    instruction: "Improve CTA and conversion copy"
                });
            }

            toast.success("Improvement started.");
            await load();
        } catch (error) {
            console.error(error);
            toast.error("Failed to start improvement.");
        } finally {
            setRunningAction("");
        }
    };

    if (loading) {
        return (
            <PageShell title="Project Intelligence">
                <div className="project-loading-card">Loading intelligence...</div>
            </PageShell>
        );
    }

    if (!data) {
        return (
            <PageShell title="Project Intelligence">
                <div className="project-empty">No intelligence data found.</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Project Intelligence">
            <div className="project-intel-shell polished">
                <section className="project-intel-hero polished">
                    <div>
                        <span className="project-intel-kicker">
                            <Sparkles size={16} />
                            AI Growth Brain
                        </span>

                        <h1>{data.businessName}</h1>

                        <p>
                            Nexus analyzed this project and prepared practical actions to improve
                            website quality, conversion, and business readiness.
                        </p>

                        <div className="module-hero-actions inline">
                            <Link className="project-action-btn secondary" to={`/crm/projects/${projectId}`}>
                                Back to Project
                            </Link>

                            <button
                                type="button"
                                className="project-action-btn primary"
                                onClick={load}
                                disabled={loading}
                            >
                                Refresh Intelligence
                            </button>
                        </div>
                    </div>

                    <div className="project-health-ring">
                        <strong>{data.healthScore}</strong>
                        <span>{data.healthLabel}</span>
                    </div>
                </section>

                <section className="project-intel-stats polished">
                    <div className="project-intel-stat">
                        <Activity size={20} />
                        <strong>{data.status || "Draft"}</strong>
                        <span>Status</span>
                    </div>

                    <div className="project-intel-stat">
                        <Target size={20} />
                        <strong>{data.totalLeads}</strong>
                        <span>Leads</span>
                    </div>

                    <div className="project-intel-stat">
                        <ShieldCheck size={20} />
                        <strong>{data.conversionRate || 0}%</strong>
                        <span>Conversion</span>
                    </div>
                </section>

                {isSuperAdmin && (
                    <section className="project-panel project-admin-intel-card">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">Super Admin Intelligence</span>
                                <h2>System & Debug View</h2>
                                <p>
                                    Inspect technical state, project identity, and available diagnostic actions.
                                </p>
                            </div>
                        </div>

                        <div className="project-admin-intel-grid polished">
                            <div>
                                <span>Project ID</span>
                                <strong>{data.projectId}</strong>
                            </div>

                            <div>
                                <span>Industry</span>
                                <strong>{data.industry || "-"}</strong>
                            </div>

                            <div>
                                <span>Status</span>
                                <strong>{data.status || "-"}</strong>
                            </div>

                            <div>
                                <span>Health Score</span>
                                <strong>{data.healthScore}</strong>
                            </div>
                        </div>

                        <div className="project-admin-actions polished">
                            <button
                                type="button"
                                className="project-action-intel"
                                onClick={() => navigate(`/crm/projects/${projectId}`)}
                            >
                                <div>
                                    <strong>Open Project Detail</strong>
                                    <span>Inspect CRM modules, variants, seed actions, and deployment controls.</span>
                                </div>
                                <ArrowRight size={18} />
                            </button>

                            <button
                                type="button"
                                className="project-action-intel"
                                onClick={() => navigate(`/crm/projects/${projectId}/modules`)}
                            >
                                <div>
                                    <strong>Inspect Modules</strong>
                                    <span>Check generated CRM modules and seed hydration output.</span>
                                </div>
                                <ArrowRight size={18} />
                            </button>
                        </div>
                    </section>
                )}

                <section className="project-intel-grid polished">
                    <div className="project-panel project-intel-card">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">AI Insights</span>
                                <h2>What Nexus Found</h2>
                                <p>Quality, conversion, and readiness signals detected by the intelligence layer.</p>
                            </div>
                        </div>

                        <div className="project-insight-list polished">
                            {(data.insights || []).length === 0 ? (
                                <div className="project-empty">No insights available yet.</div>
                            ) : (
                                (data.insights || []).map((item, index) => (
                                    <div
                                        key={`${item.title}-${index}`}
                                        className={`project-insight-item ${item.severity || ""}`}
                                    >
                                        <strong>{item.title}</strong>
                                        <p>{item.message}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="project-panel project-intel-card">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">Recommended Actions</span>
                                <h2>What To Do Next</h2>
                                <p>Run focused improvement actions without rebuilding the whole project manually.</p>
                            </div>
                        </div>

                        <div className="project-action-list polished">
                            {(data.recommendedActions || []).length === 0 ? (
                                <div className="project-empty">No recommended actions available.</div>
                            ) : (
                                (data.recommendedActions || []).map((action) => (
                                    <button
                                        key={action.actionKey}
                                        type="button"
                                        className="project-action-intel"
                                        disabled={!!runningAction}
                                        onClick={() => runAction(action.actionKey)}
                                    >
                                        <div>
                                            <strong>{action.label}</strong>
                                            <span>{action.description}</span>
                                        </div>

                                        <ArrowRight size={18} />
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </PageShell>
    );
}